import React from "react";
import "./ModernLoader.css";

interface ModernLoaderProps {
  size?: "small" | "medium" | "large";
  variant?: "primary" | "secondary" | "light" | "minimal";
  message?: string;
  overlay?: boolean;
  fullScreen?: boolean;
  className?: string;
}

/**
 * Modern loading component following DSF Portal design system
 * Replaces outdated spinning GIFs with CSS-based animations
 */
export const ModernLoader: React.FC<ModernLoaderProps> = ({
  size = "medium",
  variant = "primary",
  message,
  overlay = false,
  fullScreen = false,
  className = "",
}) => {
  const getSizeClass = () => {
    switch (size) {
      case "small":
        return "loader-small";
      case "large":
        return "loader-large";
      default:
        return "loader-medium";
    }
  };

  const getVariantClass = () => {
    switch (variant) {
      case "secondary":
        return "loader-secondary";
      case "light":
        return "loader-light";
      case "minimal":
        return "loader-minimal";
      default:
        return "loader-primary";
    }
  };

  const loaderClasses = ["modern-loader", getSizeClass(), getVariantClass(), className]
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
    "loader-container",
    overlay && "loader-overlay",
    fullScreen && "loader-fullscreen",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses} role="status" aria-live="polite">
      <div className="loader-content">
        {/* DSF Logo-based spinner */}
        <div className={loaderClasses}>
          <div className="loader-ring">
            <div className="loader-segment"></div>
            <div className="loader-segment"></div>
            <div className="loader-segment"></div>
            <div className="loader-segment"></div>
          </div>
          <div className="loader-center">
            <img
              src="/DSF_Logo-Low-trimmed2.png"
              alt=""
              className="loader-logo"
              aria-hidden="true"
            />
          </div>
        </div>

        {message && (
          <div className="loader-message" aria-label={`Loading: ${message}`}>
            {message}
          </div>
        )}
      </div>

      {/* Screen reader accessible text */}
      <span className="sr-only">
        {message ? `Loading ${message}` : "Loading content, please wait"}
      </span>
    </div>
  );
};

/**
 * Pulse loader for subtle loading states
 */
export const PulseLoader: React.FC<Omit<ModernLoaderProps, "variant">> = ({
  size = "medium",
  message,
  className = "",
}) => {
  const getSizeClass = () => {
    switch (size) {
      case "small":
        return "pulse-small";
      case "large":
        return "pulse-large";
      default:
        return "pulse-medium";
    }
  };

  return (
    <div className="pulse-loader-container" role="status" aria-live="polite">
      <div className={`pulse-loader ${getSizeClass()} ${className}`}>
        <div className="pulse-dot"></div>
        <div className="pulse-dot"></div>
        <div className="pulse-dot"></div>
      </div>

      {message && <div className="pulse-message">{message}</div>}

      <span className="sr-only">
        {message ? `Loading ${message}` : "Loading content, please wait"}
      </span>
    </div>
  );
};

/**
 * Skeleton loader for content placeholders
 */
interface SkeletonLoaderProps {
  lines?: number;
  height?: string;
  className?: string;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  lines = 3,
  height = "1rem",
  className = "",
}) => {
  return (
    <div className={`skeleton-loader ${className}`} role="status" aria-live="polite">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton-line"
          style={{
            height,
            width: index === lines - 1 ? "75%" : "100%",
          }}
        />
      ))}
      <span className="sr-only">Loading content</span>
    </div>
  );
};

/**
 * Button loader for form submissions
 */
interface ButtonLoaderProps {
  size?: "small" | "medium";
  color?: "primary" | "secondary" | "white";
}

export const ButtonLoader: React.FC<ButtonLoaderProps> = ({ size = "medium", color = "white" }) => {
  const sizeClass = size === "small" ? "btn-loader-small" : "btn-loader-medium";
  const colorClass = `btn-loader-${color}`;

  return (
    <div className={`button-loader ${sizeClass} ${colorClass}`} role="status" aria-hidden="true">
      <div className="btn-spinner"></div>
    </div>
  );
};

// Default export for backward compatibility
export default ModernLoader;
